import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
// Same stylesheet as Add New Product so the two pages are visually identical
import "./AdminAddProduct.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminAddPromotionProduct() {
  const { authFetch } = useContext(AuthContext);
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [uom, setUom] = useState("");
  const [description, setDescription] = useState("");
  const [stockQuantity, setStockQuantity] = useState("");
  const [moq, setMoq] = useState("");
  const [isIncreaseByMOQ, setIsIncreaseByMOQ] = useState(false);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [originalPrice, setOriginalPrice] = useState("");
  const [promotionPrice, setPromotionPrice] = useState("");
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const MAX_ADDITIONAL_IMAGES = 4;

  const handleAdditionalImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (additionalImages.length + files.length > MAX_ADDITIONAL_IMAGES) {
      Swal.fire(
        "Too Many Images",
        `You can upload a maximum of ${MAX_ADDITIONAL_IMAGES} additional images.`,
        "warning"
      );
      return;
    }

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire("File Too Large", "Please select images under 5MB.", "error");
        return;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["jpg", "jpeg", "png"].includes(ext)) {
        Swal.fire("Invalid File Type", "Please select JPG, JPEG or PNG images.", "error");
        return;
      }
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        setAdditionalImages((prev) => [...prev, { file, preview: reader.result }]);
      reader.readAsDataURL(file);
    });
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File Too Large", "Please select an image under 5MB.", "error");
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      Swal.fire("Invalid File Type", "Please select a JPG, JPEG or PNG image.", "error");
      return;
    }

    setMainImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setMainImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleClearMainImage = () => {
    setMainImageFile(null);
    setMainImagePreview(null);
  };

  // Live discount preview, same rule as the promotion list card
  const previewBase = parseFloat(originalPrice);
  const previewPromo = parseFloat(promotionPrice);
  const previewDiscount =
    previewBase && previewPromo && previewPromo < previewBase
      ? Math.min(99, Math.floor((1 - previewPromo / previewBase) * 100))
      : null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const promo = parseFloat(promotionPrice);
    const original = originalPrice === "" ? null : parseFloat(originalPrice);

    if (!name.trim()) {
      Swal.fire("Validation", "Please enter the product name.", "warning");
      return;
    }
    if (!promotionPrice || isNaN(promo) || promo <= 0) {
      Swal.fire("Validation", "Promotion price must be greater than 0.", "warning");
      return;
    }
    if (!mainImageFile) {
      Swal.fire("Validation", "Please upload a product image.", "warning");
      return;
    }
    if (original && promo >= original) {
      Swal.fire(
        "Validation",
        `The promotion price (RM ${promo.toFixed(2)}) must be lower than the original price (RM ${original.toFixed(2)}).`,
        "warning"
      );
      return;
    }
    if (original && promo < original * 0.1) {
      const pct = Math.min(99, Math.floor((1 - promo / original) * 100));
      const confirm = await Swal.fire({
        title: "Unusually large discount",
        html: `This is a <strong>${pct}% discount</strong>:<br/><br/>` +
          `Original: <strong>RM ${original.toFixed(2)}</strong><br/>` +
          `Promotion: <strong>RM ${promo.toFixed(2)}</strong><br/><br/>Is this correct?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, save it",
        cancelButtonText: "Let me fix it",
        confirmButtonColor: "#d33",
      });
      if (!confirm.isConfirmed) return;
    }

    const formData = new FormData();
    formData.append("PromotionPrice", promo);
    formData.append("IsActive", true);
    formData.append("Name", name.trim());
    formData.append("ProductCode", productCode.trim());
    formData.append("UOM", uom.trim());
    formData.append("Description", description.trim());
    if (stockQuantity !== "") formData.append("StockQuantity", stockQuantity);
    if (moq !== "") formData.append("MOQ", moq);
    formData.append("IsIncreaseByMOQ", isIncreaseByMOQ);
    additionalImages.forEach((img) => formData.append("AdditionalImageFiles", img.file));
    if (original !== null && !isNaN(original)) formData.append("OriginalPrice", original);
    formData.append("ImageFile", mainImageFile);

    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/Promotions`, {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        await Swal.fire({
          icon: "success",
          title: "Added",
          timer: 1500,
          showConfirmButton: false,
        });
        navigate("/admin/promotions");
      } else {
        const err = await res.json().catch(() => null);
        Swal.fire("Error", err?.message || `Failed to save promotion (${res.status})`, "error");
      }
    } catch (e) {
      Swal.fire("Error", "Failed to save promotion.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-add-product-container">
      <div className="admin-add-product-header">
        <button
          className="admin-add-product-back-btn"
          onClick={() => navigate("/admin/promotions")}
        >
          <i className="fas fa-arrow-left"></i> Back to Promotion Products
        </button>
      </div>

      <div className="admin-add-product-title">
        <h1>Add Promotion Product</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-add-product-details-form">
        <div className="admin-add-product-form-group">
          <label>Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Product name"
            required
          />
        </div>

        <div className="admin-add-product-form-group">
          <label>Product Code:</label>
          <input
            type="text"
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            placeholder="Product code (optional)"
          />
        </div>

        <div className="admin-add-product-form-group">
          <label>Product UOM:</label>
          <input
            type="text"
            value={uom}
            onChange={(e) => setUom(e.target.value)}
            placeholder="Product UOM (optional)"
          />
        </div>

        <div className="admin-add-product-form-group">
          <label>Description:</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Product description (optional)"
          />
        </div>

        <div className="admin-add-product-form-group">
          <label>Current Stock Quantity:</label>
          <input
            type="number"
            min="0"
            value={stockQuantity}
            onChange={(e) => setStockQuantity(e.target.value)}
            placeholder="Current stock quantity (optional for pre-order)"
          />
        </div>

        <div className="admin-add-product-form-group">
          <label>MOQ:</label>
          <input
            type="number"
            min="1"
            value={moq}
            onChange={(e) => setMoq(e.target.value)}
            placeholder="Minimum order quantity (optional)"
          />
        </div>

        <div className="admin-add-product-form-group" style={{ textAlign: 'left' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
            Increase By MOQ: <input
              type="checkbox"
              checked={isIncreaseByMOQ}
              onChange={(e) => setIsIncreaseByMOQ(e.target.checked)}
            />
          </label>
        </div>

        <div className="admin-add-product-form-group">
          <label>Original Price (RM):</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={originalPrice}
            onChange={(e) => setOriginalPrice(e.target.value)}
            placeholder="Original price, shown crossed out (optional)"
          />
        </div>

        <div className="admin-add-product-form-group">
          <label>Promotion Price (RM):</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={promotionPrice}
            onChange={(e) => setPromotionPrice(e.target.value)}
            placeholder="Promotion price"
            required
          />
          {previewDiscount != null && previewDiscount > 0 && (
            <p style={{ marginTop: "6px", color: "#28a745", fontSize: "13px", textAlign: "left" }}>
              {previewDiscount}% discount from original RM {previewBase.toFixed(2)}
            </p>
          )}
        </div>

        <div className="admin-add-product-form-group">
          <label>Product Images:</label>
          <div className="admin-add-product-images-row">
            <div className="admin-add-product-main-image-section">
              <div className="admin-add-product-main-image-label">Main Image</div>
              <div className="admin-add-product-image-item">
                <label htmlFor="promoMainImageUpload">
                  {mainImagePreview ? (
                    <div className="admin-add-product-image-card">
                      <img src={mainImagePreview} alt="Main Promotion Preview" />
                      <div className="admin-add-product-image-order-badge">1</div>
                    </div>
                  ) : (
                    <div className="admin-add-product-image-card">
                      <div className="admin-add-product-upload-placeholder">
                        <i className="fas fa-cloud-upload-alt"></i>
                        <div className="admin-add-product-upload-text">
                          <span>Main Image</span>
                          <span className="admin-add-product-upload-subtext">Max 5MB</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <input
                    id="promoMainImageUpload"
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageChange}
                    style={{ display: "none" }}
                  />
                </label>
                {mainImagePreview && (
                  <div className="admin-add-product-image-actions-visible">
                    <button
                      type="button"
                      className="admin-add-product-delete"
                      onClick={handleClearMainImage}
                      title="Remove image"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-add-product-additional-images-section">
              <div className="admin-add-product-additional-images-label">
                Additional Images (up to 4)
              </div>
              <div className="admin-add-product-additional-images-container">
                {additionalImages.map((img, index) => (
                  <div className="admin-add-product-image-item" key={index}>
                    <div className="admin-add-product-image-card">
                      <img src={img.preview} alt={`Additional ${index + 1}`} />
                      <div className="admin-add-product-image-order-badge">{index + 2}</div>
                    </div>
                    <div className="admin-add-product-image-actions-visible">
                      <button
                        type="button"
                        className="admin-add-product-delete"
                        onClick={() => removeAdditionalImage(index)}
                        title="Remove image"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}

                {additionalImages.length < MAX_ADDITIONAL_IMAGES && (
                  <div className="admin-add-product-image-item">
                    <label htmlFor="promoAdditionalImageUpload">
                      <div className="admin-add-product-image-card">
                        <div className="admin-add-product-upload-placeholder">
                          <i className="fas fa-plus"></i>
                          <div className="admin-add-product-upload-text">
                            <span>Add Image</span>
                            <span className="admin-add-product-upload-subtext">Max 5MB</span>
                          </div>
                        </div>
                      </div>
                    </label>
                    <input
                      id="promoAdditionalImageUpload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleAdditionalImageChange}
                      style={{ display: "none" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="admin-add-product-btn-primary"
          disabled={!name.trim() || saving}
        >
          <i className="fas fa-save"></i> {saving ? "Saving..." : "Add Promotion Product"}
        </button>
      </form>
    </div>
  );
}

export default AdminAddPromotionProduct;
